/**
 * ==========================================================================
 *  LEVEL BLOCKOUT - "Juhlanavetta"
 * ==========================================================================
 *  Axis-aligned boxes only, so collision stays cheap and 100% predictable.
 *
 *  The venue is laid out from its own site map ("Käytännön asioita"): Kimmo
 *  sleeps in EINON PIRTTI (4 on the map), the run crosses the PARKKIPAIKKA
 *  full of cars, past the PALJU that sits in front of the JUHLANAVETTA (1 and
 *  2), and ends at his own seat in the juhlasali. The LAN hall itself is
 *  modelled from the venue's floor plan, scaled off the stated room areas:
 *  juhlasali 124 m², katettu terassi 61 m², takkahuone 38.5 m², saunatilat
 *  72 m². Only the rooms the runner actually passes through are detailed; the
 *  rest are sealed volumes that give the building its real footprint.
 *
 *  Route:
 *
 *    EINON PIRTTI, MAKUUHUONE x[0..10]
 *    -> PIRTIN KÄYTÄVÄ        x[10..33]
 *    -> ULKO-OVI              x=33
 *    -> PARKKIPAIKKA          x[37..92], down the aisle between the cars
 *    -> PIHA                  x[92..118], gravelled, with the grass left at the
 *                             edges the way the venue's own photographs show it
 *    -> ANSALANKA             x[104..111], the row parked across the yard:
 *                             through the gap, or left past the bemari and over
 *                             its charging cable
 *    -> PALJUN PORTTI         x[118..126], the gap between the tub and the
 *                             woodpile, which is where Hannu comes at you
 *    -> JUHLANAVETAN OVI      (left of the kivetys, per the floor plan), with a
 *                             man asleep across the mouth of the porch
 *    -> ETEINEN   x[129.2..131.2], a 10 m corridor with three WCs off its left
 *                 and a junction at the end of it: keittiö left, karaoke on
 *    -> turn RIGHT through the opening at x=131.2, past all three WCs
 *    -> JUHLASALI x[131.55..150.5], five tables of six PCs
 *    -> KIMMON PAIKKA, the far side of the farthest table
 *
 *  The blockout runs along +X from the pirtti to the navetta and drifts towards
 *  -Z on the way, so the whole route is one long left-hand diagonal. The venue's
 *  own compass does not survive that flattening and is not meant to: what the
 *  names have to agree on is the order you pass the buildings in.
 * ==========================================================================
 */

import {
  ext,
  type CheckpointSpec,
  type DoorSpec,
  type HandleSpec,
  type LevelDefinition,
} from '../world/Level';
import { yawTowards } from '../utils/math';
import { BEDROOM_DOOR, SPAWN } from './levelDimensions';
import { ANSALANKA, ANSALANKA_ROW, BEMARI_Z, CABLE_X } from './levelAnsalanka';
import { BAARI_PARTS } from './levelBaari';
import { cylinders, smoke } from './levelCylinders';
import { decorItems } from './levelDecor';
import { COMPUTERS, KIMMO_SEAT_Z, LAN_HALL_EXTENSION } from './levelJuhlasali';
import { TELTTALEIRI } from './levelLeiri';
import {
  SLEEPING_PLAYER,
} from './levelMakuuhuone';
import {
  TUNTEMATON,
} from './levelPirtinKaapit';
import { AAMUPALA_SPEC, MUURIKKAPANNU } from './levelMuurikka';
import { RISTEYKSEN_OVET, RISTEYS, WC_SISUSTUS } from './levelNavetanOvi';
import {
  HATCH_POINT,
  KAMIINA_PIPES,
  PALJU,
  RIM_THICKNESS,
  TELINE,
  VERAJA,
  VERAJA_POST,
} from './levelPalju';
import { IKKUNALASI } from './levelPirtti';
import { SIMA_SPEC, TORNIN_OVI } from './levelPorukka';
import { SAUNAN_OVI, SAUNA_SPEC, sitters, standers } from './levelSaliVaki';
import { projection, signs } from './levelSigns';
import { sleepers, solids } from './levelSolids';
import { TELTAN_VALOT, TELTAN_YLEISO, TELTTA } from './levelTeltta';
import { floors } from './levelFloors';

/* The level's public tuning, re-exported so its call sites keep one import. */
export { TUOPPI } from './levelJuhlasali';
export { KIERTOKALUSTO, LEIRI } from './levelLeiri';
export { BED, MATTRESS_TOP } from './levelMakuuhuone';
export { MUURIKKA } from './levelMuurikka';
export { KAYTAVAN_SANKY_WIDTH, PIRTTI_ROUTE } from './levelPirtti';
export { TELTTA } from './levelTeltta';

// Priced, not eyeballed. The käytävä's high line stands Kimmo on the top of a
// bed at 1.04 m and lets him jump, and the roof slab is decor, so nothing
// stops the head that gets that high - the boards just have to be above it.
// Tallest bed plus the biggest apex the meter can buy plus the man himself is
// 1.04 + 0.93 + 1.72 = 3.69 m; `Level.test.ts` measures exactly that against
// `jumpApex(MOMENTUM.max)`, so a taller stack moves the requirement rather
// than quietly putting a head through the roof again.

/* ================================================================= doors == */

/**
 * ==========================================================================
 *  WHICH WAY A DOOR OPENS IS NOW WORTH A SECOND
 * ==========================================================================
 *  A leaf is a collider that swings - see `DOOR` and `src/world/Door.ts` - so
 *  the hinge and the sign on each of these three is a difficulty decision
 *  rather than a note about how the building was carpentered. A door that
 *  opens towards the runner sweeps a metre and a half of quarter circle
 *  through the ground he is standing on and shoves him out of it; a door that
 *  opens away from him is a hole that gets out of the way faster than he can
 *  reach it.
 *
 *  The pirtti has one of each, on purpose, thirty seconds apart:
 *
 *   - `pirtin-ulko-ovi` opens *out*, into the yard, hinged on the north jamb,
 *     and is therefore the one you take flat out: the leaf runs away from you
 *     down the arc and lands along the north side of the opening, which is the
 *     side the route is not on. It is hung on the outer face of the end wall
 *     rather than the inner one for the same reason - a door swings on the
 *     side it opens to, and a leaf hinged inside a reveal it then swings out
 *     through is a leaf drawn passing through its own frame.
 *   - `navetan-ovi` opens *in* as well, into the eteinen, away from a runner
 *     who is coming up the porstua. Which is the easy way round again, and it
 *     has to be: what the porstua costs is Mäysä, and a second toll stacked in
 *     the same 1.7 m of floor would be two things happening at one place.
 * ==========================================================================
 */
const doors: DoorSpec[] = [
  {
    id: 'pirtin-ulko-ovi',
    label: 'AVAA ULKO-OVI',
    closeLabel: 'SULJE ULKO-OVI',
    // On the outer face of `kaytava-paaty-*`, hinged on the north pieli.
    box: ext(33.18, 0.9, 33.3, 2.6, 2.15),
    hinge: [33.24, 2.6],
    swing: -1,
  },
  {
    id: 'navetan-ovi',
    label: 'AVAA JUHLANAVETAN OVI',
    closeLabel: 'SULJE JUHLANAVETAN OVI',
    box: ext(129.4, -29.9, 130.9, -29.78, 2.3),
    hinge: [129.4, -29.84],
    swing: 1,
  },
];

/**
 * And the painikkeet on the leaves that are not in that list.
 *
 * The three doors above wear the same handle and are not here: `World` works
 * theirs out from the leaf and the hinge it is already given and hangs them on
 * the leaf, because a painike on a door that opens has to go with it. These
 * are the ones on doors that never move - authored where the leaf is
 * authored, off the leaf's own box, so that moving one moves the other.
 */
const handles: HandleSpec[] = [
  ...WC_SISUSTUS.handles,
  ...RISTEYKSEN_OVET.handles,
  ...TORNIN_OVI.handles,
  ...SAUNAN_OVI.handles,
];

/* =========================================================== checkpoints == */

/**
 * The pirtti exit, DJ tent and tub yard use gates rather than doorways.
 *
 * Most of the route is a corridor two or three metres wide, so a checkpoint's
 * volume can be a small box on the fast line. Where the route forks it cannot
 * be: the split has to be stamped on both lines or it is not a split at all.
 * `CheckpointTracker` would backfill a missed one rather than stall, but a
 * split that is only ever backfilled is not a split.
 *
 * So `PIRTTI_EXIT` is the whole open end of the building - the ulko-ovi's line
 * comes through it at z ~ 1.7 and the window's along the south side at z ~ 0,
 * and past x = 33.3 there is no wall left between them - and `HANNU_PALJU` is
 * the whole strip of yard off the tub's east end, from the halkopino up to the
 * grass north of it: both ways round the tub come out on that strip, the one
 * along the north rim with the klapit in your arms and the one along the
 * halkopino without them, so both of them are stamped by the same gate.
 */
const checkpoints: CheckpointSpec[] = [
  { id: 'PIRTTI_EXIT', label: 'EINON PIRTTI', box: ext(33.4, -8.4, 34.8, 2.6, 3.0) },
  { id: 'PARKING_END', label: 'PARKKIPAIKKA', box: ext(90.5, -15.6, 93.5, -10.0, 3.0) },
  // Cover both the pallet approach and the detour around the tent.
  { id: 'DJ_TENT', label: 'DJ-TELTTA', box: ext(TELTTA.x - 1.5, TELTTA.z - 4.5, TELTTA.x - 0.1, -10.0, 3.0) },
  // Past the crate's east edge, so both the ground route and the fence jump
  // cross a clear checkpoint volume rather than one embedded in the platform.
  { id: 'HANNU_PALJU', label: 'HANNUN PALJU', box: ext(123.35, -27.5, 124.05, -16.7, 3.0) },
  { id: 'AAMUPALA', label: 'AAMUPALA', box: null },
  { id: 'LAN_HOUSE_ENTRY', label: 'ETEINEN', box: ext(129.2, -31.8, 131.2, -29.7, 3.0) },
  { id: 'LAN_ROOM_ENTRY', label: 'JUHLASALI', box: ext(131.5, RISTEYS.saliTo, 132.6, RISTEYS.saliFrom + 0.3, 3.0) },
  { id: 'SIMA', label: 'TUOPILLINEN SIMAA', box: null },
  { id: 'FINISH', label: '"KIMMON" PAIKKA', box: null },
  { id: 'PIN_CODE', label: 'PIN-KOODI', box: null },
];

/* ================================================================ level === */

export const TEST_LEVEL: LevelDefinition = {
  name: 'Juhlanavetta',
  spawn: {
    position: { x: SPAWN.x, y: 0, z: SPAWN.z },
    yaw: yawTowards(SPAWN, BEDROOM_DOOR),
  },
  bounds: ext(-2, -51, 181, 21, 12, -1),
  floors,
  solids,
  decor: decorItems,
  cylinders,
  smoke,
  doors,
  handles,
  sleepers,
  sleepingPlayer: SLEEPING_PLAYER,
  sitters,
  sprawlers: [TUNTEMATON, ...TELTAN_YLEISO],
  standers,
  campTents: TELTTALEIRI,
  muurikka: MUURIKKAPANNU,
  aamupala: AAMUPALA_SPEC,
  sima: SIMA_SPEC,
  sauna: SAUNA_SPEC,
  signs,
  screens: COMPUTERS.screens,
  hoses: [...BAARI_PARTS.hoses, ...KAMIINA_PIPES],
  projection,
  // The rig in the XLMOTO tent. Dressing, like the projector above it.
  disco: TELTAN_VALOT,
  checkpoints,
  finish: {
    // Stand beside Kimmo's chair, on the far side of the farthest table.
    zone: ext(145.1 + LAN_HALL_EXTENSION, -38.3, 147.3 + LAN_HALL_EXTENSION, -35.7, 2.2),
    // Seated at the desk, looking west at your own monitor.
    seatEye: { x: 145.85 + LAN_HALL_EXTENSION, y: 1.26, z: KIMMO_SEAT_Z },
    seatYaw: Math.PI / 2,
    seatPitch: -0.1,
  },
  powerCable: 'ansalanka-latausjohto',
  ikkuna: {
    pane: IKKUNALASI,
    // South, onto the takapiha: the shards go the way the runner was going.
    outward: -1,
    // The grass outside, which is what the takapiha's floor is laid at.
    ground: 0,
  },
  hotTub: {
    activation: {
      cableX: CABLE_X + ANSALANKA.cableHalfWidth,
      carsX: Math.max(...ANSALANKA_ROW.slice(0, 2).map((car) => car.box.maxX)),
      carsMinZ: BEMARI_Z,
      fenceX: VERAJA.x - 0.15,
    },
    center: [PALJU.x, PALJU.z],
    radius: PALJU.radius,
    // The same ring `tubRim` builds the colliders out to, which is the same
    // ring you see: the staves are the collider. Hannu walks round the outside
    // of this and climbs over it; see `Hannu.ts`.
    rimRadius: PALJU.radius + RIM_THICKNESS,
    rimHeight: PALJU.rim,
    waterLevel: 0.78,
  },
  kamiina: {
    // The top of the klapiteline's front face on the tub's north rim: the
    // corner of it a runner in the corridor actually goes past, rather than a
    // point in the middle of a rack whose far half is behind the staves.
    pile: { x: (TELINE.minX + TELINE.maxX) / 2, y: TELINE.stack, z: TELINE.backZ + TELINE.depth },
    pileHalfWidth: (TELINE.maxX - TELINE.minX) / 2,
    hatch: HATCH_POINT,
    gate: 'paljun-veraja',
    gatePost: VERAJA_POST,
    // Hinged on the south pieli, swinging out into the navetta's front yard.
    gateHinge: [VERAJA.x, VERAJA.from],
    gateSwing: 1,
  },
};
